import { useMemo } from 'react';

import type { Publication } from '~/interfaces/publication';
import useGroupSubscriptionsQuery from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useGroupSubscriptionsQuery';

import {
  EffectiveGroupSubscriptionsContext,
  type EffectiveGroupSubscriptionsContextType,
} from './EffectiveGroupSubscriptionsContext';
import { useDevScenarioContext } from './useDevScenarioContext';
import { useTabStateContext } from './useTabStateContext';

type Props = {
  publication: Publication;
  isLoggedIn: boolean;
  children: React.ReactNode;
};

/**
 * Derives the scenario-applied group list for the General tab's overlap detection, kept separate
 * from `GroupSubscriptionsProvider` so the Groups-tab domain (data, mutators, modals) and the
 * General tab's read-only view stay decoupled. Fetches the raw groups itself via the lightweight
 * `useGroupSubscriptionsQuery` (React Query, no polling/mutators), gated on the General tab being
 * active so each return to that tab re-enables the stale query and pulls a fresh list. Applies
 * `DevScenarioProvider`'s `applyGroupScenario` mapper (identity passthrough in production where the
 * scenario stays `'live'`). Renders no UI; must be mounted below `DevScenarioProvider`,
 * `PreflightProvider`, and `TabStateProvider`.
 */
export const EffectiveGroupSubscriptionsProvider = ({ publication, isLoggedIn, children }: Props) => {
  const { applyGroupScenario } = useDevScenarioContext();
  const { activeTab } = useTabStateContext();

  // BE-resolved Flipper flag (BEE-18968). Default `false` so a missing field
  // (older Swarm response) keeps the fetch off rather than hitting an endpoint
  // the publication lacks permission for.
  const groupSubscriptionsEnabled = publication.group_subscriptions_enabled ?? false;

  // Gate the fetch on the General tab being active: that tab is the only consumer
  // of `effectiveGroupSubscriptions`, and gating here means returning to it
  // re-enables the (stale) query, which refetches fresh groups each visit.
  const { groupSubscriptions } = useGroupSubscriptionsQuery({
    enabled: activeTab === 'general' && isLoggedIn && groupSubscriptionsEnabled,
  });

  const effectiveGroupSubscriptions = useMemo(
    () => applyGroupScenario(groupSubscriptions),
    [applyGroupScenario, groupSubscriptions],
  );

  const value = useMemo<EffectiveGroupSubscriptionsContextType>(
    () => ({ effectiveGroupSubscriptions }),
    [effectiveGroupSubscriptions],
  );

  return (
    <EffectiveGroupSubscriptionsContext.Provider value={value}>{children}</EffectiveGroupSubscriptionsContext.Provider>
  );
};
